import React, { useState } from "react";
import { Command as CommandPrimitive } from "cmdk";
import { CommandGroup, CommandItem, CommandList } from "../../../Components/ui/command";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import { cn } from "../../../Lib/utils";
import { MapPin, X } from "lucide-react";

// A lightweight sibling of `src/Components/ui/comboInput.jsx`'s `AutoComplete`.
// That component is hard-wired to the Dashboard order-assignment flow
// (Redux location dispatches + a nearest-rider query) — wrong for a generic
// "search an area" input, so this reuses only the Places-autocomplete
// primitives and dropdown shell, exposing a plain onPlaceSelected callback.
const AreaSearchBox = ({ onPlaceSelected }) => {
  const [open, setOpen] = useState(false);
  const {
    value,
    setValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete({
    callbackName: "NowNowZones",
    requestOptions: { componentRestrictions: { country: "ng" } },
    debounce: 300,
  });

  const handleSelect = async (address) => {
    clearSuggestions();
    setOpen(false);
    const results = await getGeocode({ address });
    const { lat, lng } = await getLatLng(results[0]);
    onPlaceSelected({
      lat,
      lng,
      address,
      viewport: results[0]?.geometry?.viewport || null,
    });
    setValue("", false);
  };

  const handleClear = () => {
    setValue("", false);
    clearSuggestions();
    setOpen(false);
  };

  return (
    <div
      className='w-[320px]'
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <CommandPrimitive className='bg-white rounded-xl shadow-md overflow-hidden'>
        <div className='flex items-center gap-2 px-3 py-2.5'>
          <MapPin size={16} className='text-primaryGreen flex-shrink-0' />
          <input
            value={value}
            placeholder='Search an area in Calabar…'
            className='w-full text-sm outline-none placeholder:text-primaryAsh'
            onChange={(e) => {
              setOpen(true);
              setValue(e.target.value);
            }}
            onBlur={() => setOpen(false)}
            autoComplete='off'
          />
          {value && (
            <button
              type='button'
              aria-label='Clear search'
              className='w-6 h-6 rounded flex items-center justify-center text-primaryAsh hover:bg-bgGreen hover:text-primaryBlack'
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={handleClear}
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div
          className={cn(
            "border-t border-lightGray max-h-[220px] overflow-auto",
            open ? "block" : "hidden",
          )}
        >
          <CommandList>
            {status === "OK" && (
              <CommandGroup>
                {data.map(({ place_id, description }) => (
                  <CommandItem
                    key={place_id}
                    value={description}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onSelect={() => handleSelect(description)}
                    className='px-3 py-2 text-sm cursor-pointer hover:bg-bgGreen'
                  >
                    {description}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </div>
      </CommandPrimitive>
    </div>
  );
};

export default AreaSearchBox;
